import { useState, useCallback, useEffect, useMemo } from 'react';
import type { Task } from '@/api/tasks';

export type TaskSortField = 'deadline' | 'client' | 'type' | 'employee';

const STORAGE_KEY = 'dsc-task-sort';
const SEARCH_STORAGE_KEY = 'dsc-task-search';
const DEFAULT_SORT: TaskSortField = 'deadline';
const VALID_SORT_FIELDS: TaskSortField[] = ['deadline', 'client', 'type', 'employee'];

function loadSortFromStorage(): TaskSortField {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data && VALID_SORT_FIELDS.includes(data as TaskSortField)) {
      return data as TaskSortField;
    }
    return DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
}

function loadSearchFromStorage(): string {
  try {
    return localStorage.getItem(SEARCH_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function saveSortToStorage(sort: TaskSortField): void {
  localStorage.setItem(STORAGE_KEY, sort);
}

function saveSearchToStorage(search: string): void {
  localStorage.setItem(SEARCH_STORAGE_KEY, search);
}

function compareByDeadline(a: Task, b: Task): number {
  // Tasks without deadline go last
  if (!a.deadline && !b.deadline) return 0;
  if (!a.deadline) return 1;
  if (!b.deadline) return -1;
  return a.deadline.localeCompare(b.deadline);
}

function compareByClient(a: Task, b: Task): number {
  // Tasks without client go last
  const aName = a.client?.name || '';
  const bName = b.client?.name || '';
  if (!aName && !bName) return 0;
  if (!aName) return 1;
  if (!bName) return -1;
  return aName.localeCompare(bName, 'ru');
}

// Type order: PERSONAL first, then OUTDOOR, then others
const TYPE_ORDER: Record<string, number> = {
  PERSONAL: 0,
  OUTDOOR: 1,
  INDOOR: 2,
};

function compareByType(a: Task, b: Task): number {
  const aOrder = TYPE_ORDER[a.type] ?? 99;
  const bOrder = TYPE_ORDER[b.type] ?? 99;
  return aOrder - bOrder;
}

function compareByEmployee(a: Task, b: Task): number {
  const aName = a.employee?.lastName || '';
  const bName = b.employee?.lastName || '';
  if (!aName && !bName) return 0;
  if (!aName) return 1;
  if (!bName) return -1;
  return aName.localeCompare(bName, 'ru');
}

function matchesSearch(task: Task, search: string): boolean {
  if (!search) return true;
  const query = search.toLowerCase();
  return (
    task.title.toLowerCase().includes(query) ||
    (task.description?.toLowerCase().includes(query) ?? false) ||
    (task.client?.name.toLowerCase().includes(query) ?? false) ||
    (task.employee?.lastName?.toLowerCase().includes(query) ?? false)
  );
}

export function useTaskSort(tasks: Task[]) {
  const [sortField, setSortField] = useState<TaskSortField>(() => loadSortFromStorage());
  const [searchQuery, setSearchQuery] = useState<string>(() => loadSearchFromStorage());

  // Persist sort to localStorage
  useEffect(() => {
    saveSortToStorage(sortField);
  }, [sortField]);

  // Persist search to localStorage (debounced effect)
  useEffect(() => {
    saveSearchToStorage(searchQuery);
  }, [searchQuery]);

  const setSort = useCallback((field: TaskSortField) => {
    setSortField(field);
  }, []);

  const setSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const sortedTasks = useMemo(() => {
    // First filter by search
    const filtered = tasks.filter((task) => matchesSearch(task, searchQuery));

    // Then sort
    const sorted = [...filtered];
    switch (sortField) {
      case 'deadline':
        sorted.sort(compareByDeadline);
        break;
      case 'client':
        sorted.sort(compareByClient);
        break;
      case 'type':
        sorted.sort(compareByType);
        break;
      case 'employee':
        sorted.sort(compareByEmployee);
        break;
    }
    return sorted;
  }, [tasks, sortField, searchQuery]);

  return {
    sortField,
    setSort,
    searchQuery,
    setSearch,
    sortedTasks,
  };
}

export const SORT_OPTIONS: { value: TaskSortField; label: string }[] = [
  { value: 'deadline', label: 'По сроку' },
  { value: 'client', label: 'По клиенту' },
  { value: 'type', label: 'По типу' },
  { value: 'employee', label: 'По сотруднику' },
];
